import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Lock, Globe } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Card } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";
import VerdictBadge from "@/components/VerdictBadge";
import { CLAIM_NEXT, VERDICTS } from "@/lib/transitions";
import { EntityLinkManager } from "@/components/EntityLinkManager";
import { LiveDataReciprocal } from "@/components/LiveDataLinks";
import { EntReciprocal } from "@/components/EntReciprocal";

export default function ClaimDetail() {
  const { id } = useParams();
  const [claim, setClaim] = useState(null);
  const [allSources, setAllSources] = useState([]);
  const [verdict, setVerdict] = useState({ verdict: "unproven", verdict_summary: "", internal_confidence: "" });
  const [ev, setEv] = useState({ description: "", is_public: true });
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selProject, setSelProject] = useState("");
  const [savingProject, setSavingProject] = useState(false);

  const load = useCallback(() => {
    api.get(`/claims/${id}`).then((r) => {
      setClaim(r.data);
      if (r.data.verdict) setVerdict({ verdict: r.data.verdict, verdict_summary: r.data.verdict_summary || "", internal_confidence: r.data.internal_confidence || "" });
    }).catch(() => {});
    api.get("/sources").then((r) => setAllSources(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])).finally(() => setProjectsLoading(false));
  }, []);
  // Keep the selector in sync with the claim's persisted project (on load and after save).
  useEffect(() => { if (claim) setSelProject(claim.project_id || ""); }, [claim]);

  const saveProject = async () => {
    setSavingProject(true);
    try {
      await api.patch(`/claims/${id}/project`, { project_id: selProject || null });
      toast.success("Project assignment saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProject(false);
    }
  };

  const transition = async (target) => {
    try { await api.post(`/claims/${id}/transition`, { target }); toast.success(`Moved to ${target}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveVerdict = async (e) => {
    e.preventDefault();
    try { await api.post(`/claims/${id}/verdict`, verdict); toast.success("Verdict saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addEvidence = async (e) => {
    e.preventDefault();
    if (!ev.description.trim()) return;
    try { await api.post(`/evidence`, { claim_id: id, ...ev }); setEv({ description: "", is_public: true }); toast.success("Evidence added"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSource = async (sid) => {
    const current = claim.source_ids || [];
    const next = current.includes(sid) ? current.filter((x) => x !== sid) : [...current, sid];
    try { await api.patch(`/claims/${id}`, { source_ids: next }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!claim) return <div className="p-6 text-zinc-500">Loading…</div>;
  const next = CLAIM_NEXT[claim.status] || [];

  return (
    <div className="p-6">
      <Link to="/command-center/claims" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Claims</Link>
      <PageHeader title="Claim Verification">
        <StatusBadge status={claim.status} />
        {claim.verdict && <VerdictBadge verdict={claim.verdict} />}
      </PageHeader>

      <Card className="p-4 mb-4">
        <p className="font-editorial text-lg italic">“{claim.statement}”</p>
        {claim.made_by && <p className="text-sm text-zinc-500 mt-1">— {claim.made_by}</p>}
        <div className="mt-4 flex flex-wrap gap-2">
          {next.map((t) => <Btn key={t} variant="ghost" data-testid={`claim-transition-${t}`} onClick={() => transition(t)}>{t.replace(/_/g, " ")}</Btn>)}
        </div>
      </Card>

      <Card className="p-4 mb-4" data-testid="project-assignment-section">
        <div className="label-caps text-zinc-500 mb-3">Research Project Assignment</div>
        <p className="text-sm text-zinc-400 mb-3">
          Currently assigned to:{" "}
          <span className="text-zinc-200 font-medium" data-testid="claim-current-project">
            {claim.project_id
              ? (projects.find((p) => p.id === claim.project_id)?.title || "Unknown project")
              : "No project assigned"}
          </span>
        </p>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <Field label="Assign to Research Project">
              <select
                data-testid="claim-project-select"
                className={inputCls}
                value={selProject}
                disabled={projectsLoading || savingProject}
                onChange={(e) => setSelProject(e.target.value)}
              >
                <option value="">— No project assigned —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </Field>
          </div>
          <Btn
            data-testid="save-project-assignment-btn"
            onClick={saveProject}
            disabled={projectsLoading || savingProject}
          >
            {savingProject ? "Saving…" : "Save Project Assignment"}
          </Btn>
        </div>
        {projectsLoading && <p className="text-xs text-zinc-600 mt-2">Loading projects…</p>}
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="label-caps text-zinc-500 mb-3">Verdict</div>
          <form onSubmit={saveVerdict} className="space-y-3">
            <Field label="Rating">
              <select data-testid="verdict-select" className={inputCls} value={verdict.verdict} onChange={(e) => setVerdict({ ...verdict, verdict: e.target.value })}>
                {VERDICTS.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            <Field label="Public verdict summary"><textarea data-testid="verdict-summary-input" className={inputCls} rows={3} value={verdict.verdict_summary} onChange={(e) => setVerdict({ ...verdict, verdict_summary: e.target.value })} /></Field>
            <Field label="Internal confidence (never public)"><input className={inputCls} value={verdict.internal_confidence} onChange={(e) => setVerdict({ ...verdict, internal_confidence: e.target.value })} /></Field>
            <Btn data-testid="verdict-save-btn" type="submit">Save Verdict</Btn>
          </form>
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-3">Evidence</div>
            <form onSubmit={addEvidence} className="space-y-2 mb-3">
              <textarea data-testid="evidence-input" className={inputCls} rows={2} value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} placeholder="Describe an evidence item…" />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-zinc-400">
                  <input type="checkbox" checked={ev.is_public} onChange={(e) => setEv({ ...ev, is_public: e.target.checked })} /> Public evidence
                </label>
                <Btn data-testid="evidence-save-btn" type="submit"><Plus className="h-4 w-4" /> Add</Btn>
              </div>
            </form>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {(claim.evidence || []).map((e) => (
                <div key={e.id} className="text-sm text-zinc-300 flex items-start gap-2">
                  {e.is_public ? <Globe className="h-3.5 w-3.5 text-emerald-400 mt-0.5" /> : <Lock className="h-3.5 w-3.5 text-red-400 mt-0.5" />}
                  <span>{e.description}</span>
                </div>
              ))}
              {(claim.evidence || []).length === 0 && <p className="text-sm text-zinc-600">No evidence yet.</p>}
            </div>
          </Card>

          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-3">Cited Sources</div>
            <div className="flex flex-wrap gap-2">
              {allSources.map((s) => (
                <button key={s.id} onClick={() => toggleSource(s.id)}
                  className={`text-xs px-2.5 py-1 rounded-sm border ${claim.source_ids?.includes(s.id) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}>
                  {s.title}
                </button>
              ))}
              {allSources.length === 0 && <span className="text-xs text-zinc-600">No sources in library.</span>}
            </div>
          </Card>

          <EntityLinkManager linkType="claim" recordId={id} note="Link canonical Entity Profiles to this claim. An editorial association only — it implies no guilt, endorsement, authorship, responsibility or verification." />

          <LiveDataReciprocal kind="claim" id={id} />
          <EntReciprocal kind="claim" id={id} />
        </div>
      </div>
    </div>
  );
}
